import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignup } from '@/services/auth.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTranslation } from 'react-i18next';
import { SproutIcon, LoaderIcon, AlertCircleIcon } from 'lucide-react';
import logo from '@/assets/Humeos_logotype_RGB_positive.png';
import { BASE } from '../../basePath';

export default function Signup() {
  const navigate = useNavigate();
  const { mutate: signup, error, isPending } = useSignup();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const siteKey =
    import.meta.env.VITE_RECAPTCHA_SITE_KEY ||
    '6Lda7_sqAAAAAGifILMp7wVt2PkLGfhSkXJNr7xU';
  const { t } = useTranslation();

  useEffect(() => {
    const existingScript = document.querySelector(
      `script[src*="recaptcha/enterprise.js"]`,
    );
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.grecaptcha.enterprise.ready(() => {
          setIsRecaptchaLoaded(true);
        });
      };
      document.head.appendChild(script);
      return () => {
        document.head.removeChild(script);
      };
    } else {
      window.grecaptcha.enterprise.ready(() => {
        setIsRecaptchaLoaded(true);
      });
    }
  }, [siteKey]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isRecaptchaLoaded) return;
    try {
      const token = await window.grecaptcha.enterprise.execute(siteKey, {
        action: 'auth',
      });
      signup({
        email,
        password,
        firstName,
        lastName,
        recaptchaToken: token,
      });
    } catch (err) {
      console.error('reCAPTCHA error:', err);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] h-screen">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-[#0f1f17] via-[#143523] to-[#1f4a32] text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '24px 24px',
          }}
        />
        <div className="relative flex items-center gap-3">
          <img src={logo} alt="Humeos" className="h-9 w-9" />
          <div className="text-lg font-semibold">Humeos</div>
        </div>
        <div className="relative space-y-6 max-w-md">
          <SproutIcon className="h-12 w-12 text-[#74c69d]" />
          <h1 className="text-4xl font-semibold leading-tight tracking-tight">
            {t('Get a workspace for your team.')}
          </h1>
          <p className="text-white/70 text-base leading-relaxed">
            {t(
              'Manage scanners, samples, and image processing pipelines in one place.',
            )}
          </p>
        </div>
        <div className="relative text-xs text-white/40">
          © {new Date().getFullYear()} Humeos
        </div>
      </div>

      <div className="bg-white flex items-center justify-center p-8 overflow-y-auto">
        <form onSubmit={handleSignup} className="w-full max-w-sm space-y-6">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={logo} alt="Humeos" className="h-8 w-8" />
            <span className="font-semibold text-[#1c2620]">Humeos</span>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-[#1c2620]">
              {t('Create your account')}
            </h2>
            <p className="text-sm text-[#5a6259] mt-1">
              {t('Just a few details to get started.')}
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2.5 p-3 rounded-md bg-red-50 border border-red-100 text-sm text-red-700">
              <AlertCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error.message}</span>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName">{t('Firstname')}</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName">{t('Lastname')}</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">{t('Email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@lab.org"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">{t('Password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={!isRecaptchaLoaded || isPending}
            className="w-full h-10 bg-[#2d6a4f] hover:bg-[#245741] text-white"
          >
            {isPending && <LoaderIcon className="h-4 w-4 mr-2 animate-spin" />}
            {t('Create account')}
          </Button>

          <div className="text-sm text-center text-[#5a6259]">
            {t('Already have an account?')}{' '}
            <button
              type="button"
              onClick={() => navigate(`${BASE}/login`)}
              className="text-[#2d6a4f] font-medium hover:underline"
            >
              {t('Sign in')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
